const baseUrl = process.env.VUE_APP_SOCKET_URL;
/**
 * SSE (Server-Sent Events) 请求工具
 * 用于处理服务器发送事件流
 */
class SSEConnection {
  constructor(url, options = {}) {
    this.url = url;
    this.options = {
      headers: {},
      withCredentials: false,
      ...options,
    };

    this.eventSource = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onOpenCallback = null;

    // 用于处理跨chunk的SSE数据
    this.buffer = '';
    this.currentEvent = null;
    this.currentData = null;
  }

  /**
   * 连接SSE
   */
  connect() {
    if (this.isConnected) {
      console.warn('SSE连接已存在');
      return;
    }

    try {
      // 检查是否有自定义headers
      if (Object.keys(this.options.headers).length > 0) {
        // 使用fetch API创建带自定义headers的SSE连接
        this.createEventSourceWithHeaders();
      } else {
        // 使用标准EventSource
        this.eventSource = new EventSource(baseUrl + this.url);
        this.setupEventListeners();
      }
    } catch (error) {
      console.error('SSE连接失败:', error);
      this.handleError(error);
    }
  }

  /**
   * 使用fetch API创建带自定义headers的SSE连接
   */
  createEventSourceWithHeaders() {
    const headers = new Headers();

    // 添加自定义headers
    Object.keys(this.options.headers).forEach((key) => {
      headers.append(key, this.options.headers[key]);
    });

    fetch(baseUrl + this.url, {
      method: 'GET',
      headers: headers,
      credentials: this.options.withCredentials ? 'include' : 'omit',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        // 模拟EventSource的事件处理
        this.isConnected = true;

        // 重置缓冲区状态
        this.buffer = '';
        this.currentEvent = null;
        this.currentData = null;

        // console.log('SSE连接已建立');

        if (this.onOpenCallback) {
          this.onOpenCallback({ type: 'open' });
        }

        const processStream = () => {
          reader
            .read()
            .then(({ done, value }) => {
              if (done) {
                // console.log('SSE流结束');
                this.isConnected = false;
                return;
              }

              const chunk = decoder.decode(value, { stream: true });
              this.buffer += chunk;

              // 处理缓冲区中的完整行
              this.processBuffer();

              processStream();
            })
            .catch((error) => {
              console.error('SSE读取错误:', error);
              this.isConnected = false;
              if (this.onErrorCallback) {
                this.onErrorCallback(error);
              }
              this.triggerEventListeners('error', error);
            });
        };

        processStream();
      })
      .catch((error) => {
        console.error('SSE连接失败:', error);
        this.isConnected = false;

        if (this.onErrorCallback) {
          this.onErrorCallback(error);
        }
        this.triggerEventListeners('error', error);
      });
  }

  /**
   * 设置事件监听器
   */
  setupEventListeners() {
    // 连接打开
    this.eventSource.onopen = (event) => {
      this.isConnected = true;
      // console.log('SSE连接已建立');
      if (this.onOpenCallback) {
        this.onOpenCallback(event);
      }
    };

    // 接收消息
    this.eventSource.onmessage = (event) => {
      // 对于标准EventSource，直接调用processSSEMessage方法
      this.processSSEMessage('message', event.data);
    };

    // 错误处理
    this.eventSource.onerror = (event) => {
      console.error('SSE连接错误:', event);
      this.isConnected = false;

      if (this.onErrorCallback) {
        this.onErrorCallback(event);
      }

      this.triggerEventListeners('error', event);
    };
  }

  /**
   * 关闭连接
   */
  close() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.isConnected = false;

    // 重置缓冲区状态
    this.buffer = '';
    this.currentEvent = null;
    this.currentData = null;

    // console.log('SSE连接已关闭');
  }

  /**
   * 添加事件监听器
   * @param {string} eventName 事件名称
   * @param {function} callback 回调函数
   */
  addEventListener(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, []);
    }
    this.listeners.get(eventName).push(callback);
  }

  /**
   * 移除事件监听器
   * @param {string} eventName 事件名称
   * @param {function} callback 回调函数
   */
  removeEventListener(eventName, callback) {
    if (this.listeners.has(eventName)) {
      const callbacks = this.listeners.get(eventName);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * 触发事件监听器
   * @param {string} eventName 事件名称
   * @param {...any} args 参数
   */
  triggerEventListeners(eventName, ...args) {
    if (this.listeners.has(eventName)) {
      this.listeners.get(eventName).forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`SSE事件监听器错误 [${eventName}]:`, error);
        }
      });
    }
  }

  /**
   * 设置消息回调
   * @param {function} callback 回调函数
   */
  onMessage(callback) {
    this.onMessageCallback = callback;
  }

  /**
   * 设置错误回调
   * @param {function} callback 回调函数
   */
  onError(callback) {
    this.onErrorCallback = callback;
  }

  /**
   * 处理SSE缓冲区数据
   */
  processBuffer() {
    // 查找完整的行（以换行符结尾）
    let newlineIndex;
    while ((newlineIndex = this.buffer.indexOf('\n')) !== -1) {
      const line = this.buffer.substring(0, newlineIndex).trim();
      this.buffer = this.buffer.substring(newlineIndex + 1);

      // console.log('SSE处理行:', line);

      if (line.startsWith('event:')) {
        // 如果之前有完整的事件数据，先处理它
        if (
          this.currentEvent &&
          this.currentData !== null &&
          this.currentData !== ''
        ) {
          // console.log('处理完整事件:', this.currentEvent, this.currentData);
          this.processSSEMessage(this.currentEvent, this.currentData);
        }
        // 开始新的事件
        this.currentEvent = line.substring(6).trim(); // 去掉 'event:' 前缀
        this.currentData = null;
        // console.log('开始新事件:', this.currentEvent);
      } else if (line.startsWith('data:')) {
        // 获取数据内容，支持多行数据
        const dataContent = line.substring(5).trim(); // 去掉 'data:' 前缀
        // console.log('解析data行，内容:', dataContent, '长度:', dataContent.length);
        if (this.currentData === null) {
          this.currentData = dataContent;
        } else {
          // 如果已经有数据，则追加（多行数据的情况）
          this.currentData += '\n' + dataContent;
        }
        // console.log('当前累积的data:', this.currentData);
      } else if (line === '') {
        // 空行表示事件结束，处理当前事件
        if (
          this.currentEvent &&
          this.currentData !== null &&
          this.currentData !== ''
        ) {
          // console.log('空行触发事件处理:', this.currentEvent, this.currentData);
          this.processSSEMessage(this.currentEvent, this.currentData);
          this.currentEvent = null;
          this.currentData = null;
        }
      }
    }
  }

  /**
   * 设置连接打开回调
   * @param {function} callback 回调函数
   */
  onOpen(callback) {
    this.onOpenCallback = callback;
  }

  /**
   * 处理SSE消息
   * @param {string} eventType 事件类型
   * @param {string} data 事件数据
   */
  processSSEMessage(eventType, data) {
    // console.log('处理SSE消息:', {
    //   event: eventType,
    //   data: data,
    //   dataType: typeof data,
    //   dataLength: data ? data.length : 0,
    // });

    // 智能解析JSON数据
    let parsedData = this.parseSSEData(data);

    // 组装完整的消息对象
    const messageObject = {
      event: eventType,
      data: parsedData,
      rawData: data,
    };

    // 调用回调函数
    if (this.onMessageCallback) {
      this.onMessageCallback(messageObject, {
        event: eventType,
        data: data,
        parsedData: parsedData,
      });
    }

    // 触发事件监听器
    this.triggerEventListeners(eventType, messageObject, {
      event: eventType,
      data: data,
      parsedData: parsedData,
    });

    // 同时触发通用的message事件
    this.triggerEventListeners('message', messageObject, {
      event: eventType,
      data: data,
      parsedData: parsedData,
    });
  }

  /**
   * 智能解析SSE数据
   * @param {string|object} data 原始数据
   * @returns {object|string} 解析后的数据
   */
  parseSSEData(data) {
    // 如果已经是对象，直接返回
    if (typeof data === 'object' && data !== null) {
      return data;
    }

    // 如果是字符串，先去除首尾空白字符
    if (typeof data === 'string') {
      data = data.trim();

      // 如果数据为空，返回空字符串
      if (data === '') {
        return data;
      }

      // 如果看起来像JSON，尝试解析
      if (data.startsWith('{') || data.startsWith('[')) {
        try {
          const parsed = JSON.parse(data);
          // console.log('JSON解析成功:', parsed);
          return parsed;
        } catch (error) {
          console.warn('SSE数据JSON解析失败:', error, '原始数据长度:', data.length);

          // 尝试修复常见的JSON问题
          let fixedData = data;

          // 检查是否缺少结束括号
          if (data.startsWith('{') && !data.endsWith('}')) {
            console.warn('检测到不完整的JSON对象，尝试修复...');
            // 这里可以添加更复杂的修复逻辑
          }

          // 解析失败时返回原始字符串
          return data;
        }
      }

      // 其他情况返回原始字符串
      return data;
    }

    // 其他情况返回原始数据
    return data;
  }

  /**
   * 获取连接状态
   * @returns {boolean}
   */
  getConnectionState() {
    return this.isConnected;
  }
}

/**
 * 创建SSE连接的便捷函数
 * @param {string} url SSE服务器地址
 * @param {object} options 配置选项
 * @returns {SSEConnection}
 */
export function createSSEConnection(url, options = {}) {
  return new SSEConnection(url, options);
}

/**
 * 默认导出SSEConnection类
 */
export default SSEConnection;
